import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import axiosInstance from "../common/axiosInstance";
import {
  ApiResponse,
  OrgAdminProfile,
  OrgUserProfile,
  rolesObjType,
  User,
} from "../types/api";

import { usePermissionStore } from "../store/usePermissionStore";
import { usePermissionSeedStore } from "../store/usePermissionSeedStore";

type AuthContextType = {
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  user: User | null;
  setUser: (user: User | null) => void; // Add this
  orgAdmin: OrgAdminProfile | null;
  setOrgAdmin: (orgAdminUser: OrgAdminProfile | null) => void;
  orgUser: OrgUserProfile | null;
  setOrgUser: (orgUser: OrgUserProfile | null) => void;
  loading: boolean;
  logout: () => void;
  roleData: rolesObjType | null;
  setRoleData: (role: rolesObjType | null) => void;
  userType: string;
  setUserType: (userType: string) => void;
  refreshUserData: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [orgAdmin, setOrgAdmin] = useState<OrgAdminProfile | null>(null);
  const [orgUser, setOrgUser] = useState<OrgUserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [roleData, setRoleData] = useState<rolesObjType | null>(null);
  const [userType, setUserType] = useState<string>("");

  useEffect(() => {
    const checkAuth = async () => {

      setLoading(true);
      try {
        const response = await axiosInstance.get<ApiResponse<User>>(
          `/api/tenant/me`
        )

        if (response.status === 200 && response.data.message) {
          setIsAuthenticated(true);
          setUser(response.data.message);

          if (response.data?.permissionSeed) {
            usePermissionSeedStore.getState().setSeed(response.data.permissionSeed);
          }

          // Determine the most relevant role for userType
          const roles = response.data.message.roles;
          if (roles.includes("superadmin")) {
            setUserType("superadmin");
          } else if (roles.includes("orgadmin")) {
            setOrgAdmin(response.data.message as any);
            setUserType("orgadmin");
          } else if (roles.includes("orguser")) {
            setUserType("orguser");
            setOrgUser(response.data.message as any);
            if (response.data?.roleData) {
              const perms = response.data.roleData;
              setRoleData(perms)
              usePermissionStore
                .getState()
                .setPermissions(perms?.permissions || {});
            }
          } else {
            setUserType(""); // Default for other roles

          }
        } else {
          // Fallback to stored data if server request fails but we have valid token
          setIsAuthenticated(false);
          setUser(null);
          setOrgAdmin(null);
          setOrgUser(null);
          setRoleData(null);
          setUserType("");
        }
      } catch (err) {
        console.error("Auth check failed:", err);
        setIsAuthenticated(false);
        setUser(null);
        setOrgAdmin(null);
        setOrgUser(null);
        setRoleData(null);
        setUserType("");
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // useEffect(() => {
  // }, [roleData]);

  const refreshUserData = async () => {
    try {

      const response = await axiosInstance.get<ApiResponse<User>>(
        `/api/tenant/me`,
      );

      if (response.status === 200 && response.data.message) {
        setUser(response.data.message);

        if (response.data?.permissionSeed) {
          usePermissionSeedStore.getState().setSeed(response.data.permissionSeed);
        }

        const roles = response.data.message.roles;
        if (roles.includes("orgadmin")) {
          setOrgAdmin(response.data.message as any);
        } else if (roles.includes("orguser")) {
          setOrgUser(response.data.message as any);
        }
      }
    } catch (err) {
      console.error("Failed to refresh user data:", err);
    }
  };

  const logout = async () => {
    try {

      await axiosInstance.post(`/api/tenant/logout`, {});

    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      setUserType("");
      setRoleData(null);
      setOrgAdmin(null);
      setOrgUser(null);
      usePermissionSeedStore.getState().clearSeed();
      usePermissionStore.getState().clearPermissions();

      // Navigate to login page
      window.location.href = "/login";
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        user,
        orgAdmin,
        setOrgAdmin,
        orgUser,
        setOrgUser,
        roleData,
        setRoleData,
        userType,
        setUserType,
        setUser,
        loading,
        logout,
        refreshUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
